/**
 * THE CHARTER TEMPLATE — the sections a project charter scaffolds with.
 *
 * When a charter is authored in the app rather than uploaded, it starts on this
 * skeleton so a PM or BA is not staring at a blank page: the sections a real
 * charter carries (PM Witness audit, 12 Sep — PMBOK/PRINCE2, adapted for an ICT
 * department), each an empty heading to write under. It is a starting shape, not
 * a cage — every section can be rewritten, reordered or removed, exactly as the
 * plan can.
 *
 * SCOPE, ASSUMPTIONS AND CONSTRAINTS ARE NOT HERE — they are set once on "About
 * this project", where the requirements document already reads them, and are
 * woven into the charter at their usual places when it is read or produced (see
 * `composeCharter`). A PM sets them in one place, not two.
 */
import type { RichDoc } from './rich-text';
import type { JSONContent } from '@tiptap/core';
import { richHasText, richText } from './rich-text';
import type { Project } from './projects';
import type { RiskForCharter } from './raid';

const h2 = (text: string): JSONContent => ({
  type: 'heading',
  attrs: { level: 2 },
  content: [{ type: 'text', text }],
});
const h3 = (text: string): JSONContent => ({
  type: 'heading',
  attrs: { level: 3 },
  content: [{ type: 'text', text }],
});
const p = (text = ''): JSONContent => ({
  type: 'paragraph',
  ...(text ? { content: [{ type: 'text', text }] } : {}),
});
const cell = (text: string, header: boolean): JSONContent => ({
  type: header ? 'tableHeader' : 'tableCell',
  content: [{ type: 'paragraph', ...(text ? { content: [{ type: 'text', text }] } : {}) }],
});
const row = (cells: string[], header = false): JSONContent => ({
  type: 'tableRow',
  content: cells.map((c) => cell(c, header)),
});
/** A table with a header row and one empty row to show its shape. */
const table = (headers: string[]): JSONContent => ({
  type: 'table',
  content: [row(headers, true), row(headers.map(() => ''))],
});

export const CHARTER_TEMPLATE: RichDoc = {
  type: 'doc',
  content: [
    h2('Purpose & justification'),
    p(),
    h2('Objectives & success criteria'),
    p(),
    h2('Milestones'),
    table(['Milestone', 'Target date']),
    // The Risk register is NOT authored here — it is the project's first-class risk
    // register (0193), the single source of truth. composeCharter renders its OPEN
    // risks into this charter at read/produce time, exactly as it weaves Scope /
    // Assumptions / Constraints from About. A PM adds and changes risks in the
    // register, never in two places.
    h2('Roles & responsibilities (RACI)'),
    table(['Activity', 'Responsible', 'Accountable', 'Consulted', 'Informed']),
    h2('Governance & sign-off'),
    p(),
  ],
};

/**
 * WEAVE THE ABOUT-SOURCED SECTIONS INTO THE CHARTER — the single source of truth.
 *
 * Scope, assumptions and constraints live on "About this project"; the charter
 * does not ask for them again. This drops them into the authored narrative at the
 * places a charter states them — Scope after the objectives, Assumptions and
 * Constraints after the milestones — so the document a reader (and the signed
 * PDF) sees is complete and in the conventional order, while a PM only ever sets
 * them once.
 *
 * The About fields are read at the moment the charter is read or produced, exactly
 * as the sponsor and budget on the parameter block are. It is PURE — the same
 * authored body and the same About values always compose the same document, so a
 * produced charter matches the fingerprint it was signed against (a charter is
 * only ever produced while a draft, so a later About edit cannot change already-
 * signed bytes).
 */

/** The concatenated text of a heading node, for matching an insertion anchor. */
function headingText(node: JSONContent): string {
  if (node.type !== 'heading' || node.attrs?.level !== 2) return '';
  return (node.content ?? [])
    .map((c) => (c.type === 'text' ? (c.text ?? '') : ''))
    .join('')
    .trim()
    .toLowerCase();
}

/** A heading node that begins with any of the given (lower-cased) prefixes. */
const headingStarts = (node: JSONContent, ...prefixes: string[]): boolean => {
  const t = headingText(node);
  return t !== '' && prefixes.some((p) => t.startsWith(p));
};

/** The block content of an About field, or a muted "Not set." when it is blank —
 *  so an unfilled section still reads as a section, never a silent gap. */
function bodyOf(doc?: RichDoc | null): JSONContent[] {
  if (richHasText(doc)) return doc!.content ?? [];
  return [{ type: 'paragraph', content: [{ type: 'text', text: 'Not set.', marks: [{ type: 'italic' }] }] }];
}

function scopeBlocks(project?: Project): JSONContent[] {
  return [
    h2('Scope'),
    h3('In scope'),
    ...bodyOf(project?.scopeIn),
    h3('Out of scope'),
    ...bodyOf(project?.scopeOut),
  ];
}
function assumptionsConstraintsBlocks(project?: Project): JSONContent[] {
  return [h2('Assumptions'), ...bodyOf(project?.assumptions), h2('Constraints'), ...bodyOf(project?.constraints)];
}

/** The Risk-register section rendered FROM the register (0193) — the open risks,
 *  worst-first, as `risksForCharter` ordered them. An empty register reads as a
 *  stated section, never a silent gap, so the charter always shows it has one. */
function riskRegisterBlocks(risks: RiskForCharter[]): JSONContent[] {
  if (risks.length === 0) {
    return [h2('Risk register'), p('No risks are recorded in the risk register yet.')];
  }
  const headers = ['Risk', 'Likelihood', 'Impact', 'Severity', 'Owner', 'Mitigation'];
  const rows: JSONContent[] = [row(headers, true)];
  for (const r of risks) {
    rows.push(row([r.risk, r.likelihood, r.impact, r.severity, r.owner, r.mitigation]));
  }
  return [h2('Risk register'), { type: 'table', content: rows }];
}

/** Drop any authored "Risk register" heading and the table under it, so the
 *  register-rendered section replaces it rather than doubling it (a legacy charter
 *  that still has an authored table is migrated in the render; its stored body is
 *  left untouched). */
function stripRiskSection(content: JSONContent[]): JSONContent[] {
  const out: JSONContent[] = [];
  for (let i = 0; i < content.length; i++) {
    if (headingStarts(content[i], 'risk')) {
      // the heading, and the table immediately under it if there is one
      if (content[i + 1]?.type === 'table') i++;
      continue;
    }
    out.push(content[i]);
  }
  return out;
}

/** A charter's authored body with any legacy "Risk register" heading + table removed, for the
 *  EDITABLE view (UX-H2/FF-3). Risks are the register's now (0193); the read view and the PDF
 *  already strip an authored table and weave the register in, so editing one in the body does
 *  nothing. Showing the editor the stripped body means a PM never edits a risk table that is
 *  silently ignored — the FromRegister panel points them to where risks actually live. */
export function withoutAuthoredRiskSection(doc?: RichDoc | null): RichDoc {
  return { type: 'doc', content: stripRiskSection(doc?.content ?? []) };
}

/**
 * Weave the About-sourced sections and the register's risks into the charter.
 *
 * `risks` is the project's OPEN risks, pre-ordered by `risksForCharter`. When it is
 * passed (a DRAFT render or a PRODUCE), any authored Risk-register table is stripped
 * and the register-rendered section is inserted in its place — so the charter reflects
 * whatever the register holds, with no in-charter editing. When it is OMITTED (a SIGNED
 * charter, which must show exactly what it was signed with), the authored body is left
 * as-is: a charter signed under the register shows no on-screen risk table — its risks
 * as signed live in the approved PDF — while a legacy charter keeps its frozen table.
 */
export function composeCharter(authored?: RichDoc | null, project?: Project, risks?: RiskForCharter[]): RichDoc {
  const weaveRisks = risks !== undefined;
  const src = weaveRisks ? stripRiskSection(authored?.content ?? []) : (authored?.content ?? []);
  const scope = scopeBlocks(project);
  const ac = assumptionsConstraintsBlocks(project);
  const riskBlocks = weaveRisks ? riskRegisterBlocks(risks) : [];
  const out: JSONContent[] = [];
  let scopeDone = false;
  let acDone = false;
  let risksDone = !weaveRisks;

  for (const node of src) {
    // Scope states itself after the objectives — i.e. before Milestones, or the
    // next major section if the author removed Milestones.
    if (!scopeDone && headingStarts(node, 'milestone', 'risk', 'role', 'governance')) {
      out.push(...scope);
      scopeDone = true;
    }
    // Assumptions and constraints follow the milestones — before the risk
    // register, or the next major section if that too was removed.
    if (!acDone && headingStarts(node, 'risk', 'role', 'governance')) {
      out.push(...ac);
      acDone = true;
    }
    // The register's risks sit after the constraints — before Roles/Governance.
    if (!risksDone && headingStarts(node, 'role', 'governance')) {
      out.push(...riskBlocks);
      risksDone = true;
    }
    out.push(node);
  }
  // If an anchor was missing, the section is never dropped — it lands at the end,
  // still in scope-then-assumptions-then-risks order.
  if (!scopeDone) out.push(...scope);
  if (!acDone) out.push(...ac);
  if (!risksDone) out.push(...riskBlocks);

  return { type: 'doc', content: out.length ? out : [{ type: 'paragraph' }] };
}

/**
 * ONE RISK, READ OUT OF THE CHARTER'S RISK REGISTER — the register's columns
 * (CHARTER_TEMPLATE above) as plain text, so a captured risk can leave the
 * document and be reported on. A charter risk is authored as a table row, not a
 * typed object; this is that row, read.
 */
export interface CharterRisk {
  risk: string;
  likelihood: string;
  impact: string;
  /** The derived severity band word (Critical/High/Medium/Low) from the register's
   *  one L×I scale, or '' when unassessed or sourced from a legacy authored table that
   *  predates the scale. The steering surfaces show THIS, not the impact word (Q2). */
  severity: string;
  owner: string;
  mitigation: string;
}

/**
 * HOW MANY RISKS A SUMMARY SHOWS before the rest become "+N more on the charter".
 * The ONE cap both surfaces read — the portfolio status drill and the steering-pack
 * PDF — so the overflow sentence can never cite two different counts for the same
 * project (it did: the drill capped at 6 and the pack at 5). Changed here, it moves
 * in both places at once.
 */
export const RISK_CAP = 6;

/**
 * THE CHARTER'S RISK REGISTER, READ AS DATA — so the capture is not stranded in
 * the document. The register is a rich-text table under the "Risk register"
 * heading (CHARTER_TEMPLATE), one row per risk; this finds that table and returns
 * a CharterRisk per row that carries anything, dropping the template's blank
 * starter row and the header. Returns `[]` when the charter has no register, an
 * empty one, is uploaded-as-a-file (no authored `charter`), or is absent — so a
 * caller can always map over the result. Pure: the same document always reads the
 * same rows, which is what lets the portfolio roll these up without re-reading the
 * editor.
 */
export function charterRisks(doc?: RichDoc | null): CharterRisk[] {
  const content = doc?.content ?? [];
  // The first table AFTER the "Risk register" heading — not any table in the doc,
  // so a renamed-away register or a Milestones/RACI table is never mistaken for it.
  let riskTable: JSONContent | undefined;
  for (let i = 0; i < content.length; i++) {
    if (headingStarts(content[i], 'risk')) {
      riskTable = content.slice(i + 1).find((n) => n.type === 'table');
      break;
    }
  }
  const out: CharterRisk[] = [];
  for (const r of riskTable?.content ?? []) {
    if (r.type !== 'tableRow') continue;
    const cells = (r.content ?? []).filter((c) => c.type === 'tableCell' || c.type === 'tableHeader');
    // The header row is all header cells — the column names, not a risk.
    if (cells.length === 0 || cells.every((c) => c.type === 'tableHeader')) continue;
    const at = (i: number): string => (cells[i] ? richText(cells[i]).trim() : '');
    // Legacy authored tables are five columns (Risk, Likelihood, Impact, Owner,
    // Mitigation) — no Severity column, so it reads ''. The register-rendered table is
    // render-only (stripped + replaced, never parsed back), so this path only ever sees
    // the legacy shape.
    const entry: CharterRisk = {
      risk: at(0),
      likelihood: at(1),
      impact: at(2),
      severity: '',
      owner: at(3),
      mitigation: at(4),
    };
    // A wholly empty row (the template's starter) is not a risk.
    if (Object.values(entry).some((v) => v !== '')) out.push(entry);
  }
  return out;
}
